import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadIcon from '@mui/icons-material/Upload'
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import B2bFileUploadApi, {
  UploadTenantFileResponse
} from 'api/b2b/B2bFileUploadApi'
import B2bTDPAPI from 'api/b2b/TdpApi'
import OrbitSession from 'hooks/useCookies'

export function EditTdpDocuments() {
  const token = OrbitSession.get('faberToken')
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const tenantId = searchParams.get('id') || ''

  type FormValues = {
    companyLogo: File | string | null
    registrationCert: File | string | null
    taxCert: File | string | null
    signatoryDoc: File | string | null
  }

  type UploadStatus = {
    [key: string]: {
      loading: boolean
      error: string | null
      uploaded: boolean
      hasExisting: boolean // Track if there's an existing file
    }
  }

  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      companyLogo: null,
      registrationCert: null,
      taxCert: null,
      signatoryDoc: null
    }
  })

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    companyLogo: {
      loading: false,
      error: null,
      uploaded: false,
      hasExisting: false
    },
    registrationCert: {
      loading: false,
      error: null,
      uploaded: false,
      hasExisting: false
    },
    taxCert: {
      loading: false,
      error: null,
      uploaded: false,
      hasExisting: false
    },
    signatoryDoc: {
      loading: false,
      error: null,
      uploaded: false,
      hasExisting: false
    }
  })

  const documentTypeMap: Record<
    string,
    'b_logo' | 'b_cert' | 't_cert' | 'a_sign'
  > = {
    companyLogo: 'b_logo',
    registrationCert: 'b_cert',
    taxCert: 't_cert',
    signatoryDoc: 'a_sign'
  }

  // Watch form values to detect changes
  const watchedValues = watch()

  const createUploadFunction = (key: string) => {
    return async (file: File) => {
      const documentType = documentTypeMap[key]

      const formData = new FormData()
      formData.append('files', file)

      const queryString = new URLSearchParams({
        tenant_id: tenantId,
        document_type: documentType
      }).toString()

      return B2bFileUploadApi.uploadTenantDocuments(
        formData,
        queryString,
        token
      )
    }
  }

  // Query for fetching the existing document data
  const { data: tenantData, isLoading: tenantLoading } = useQuery(
    ['specific-tenant-details', tenantId],
    async () => {
      const { data } = await B2bTDPAPI.getTDPDetailsById(token, tenantId)
      return data.TenantDetails
    },
    {
      enabled: !!tenantId,
      staleTime: 1000 * 60 * 30,
      cacheTime: 1000 * 60 * 60,
      retry: 1,
      onError: (error) => {
        console.error('Failed to fetch tenant details:', error)
      }
    }
  )

  // TODO:Can we make these muation generic instead of one for each?
  const companyLogoMutation = useMutation({
    mutationFn: createUploadFunction('companyLogo')
  })

  const registrationCertMutation = useMutation({
    mutationFn: createUploadFunction('registrationCert')
  })

  const taxCertMutation = useMutation({
    mutationFn: createUploadFunction('taxCert')
  })

  const signatoryDocMutation = useMutation({
    mutationFn: createUploadFunction('signatoryDoc')
  })

  const mutations = {
    companyLogo: companyLogoMutation,
    registrationCert: registrationCertMutation,
    taxCert: taxCertMutation,
    signatoryDoc: signatoryDocMutation
  }

  const handleFileUpload = async (data: FormValues) => {
    const fileFields = [
      { key: 'companyLogo', file: data.companyLogo },
      { key: 'registrationCert', file: data.registrationCert },
      { key: 'taxCert', file: data.taxCert },
      { key: 'signatoryDoc', file: data.signatoryDoc }
    ]

    // Only upload files that are File objects (newly uploaded), not strings (existing URLs)
    const filesToUpload = fileFields.filter(({ file }) => file instanceof File)

    if (filesToUpload.length === 0) {
      toast.info('No new documents to upload.', {
        autoClose: 3000,
        hideProgressBar: false
      })
      queryClient.invalidateQueries({
        queryKey: [['specific-tenant-details', tenantId]]
      })
      // navigate('/hq/b2b')
      return
    }

    const uploadPromises = filesToUpload.map(({ key, file }) => {
      const mutation = mutations[key as keyof typeof mutations]
      return mutation.mutateAsync(file as File)
    })

    try {
      const results = await Promise.allSettled(uploadPromises)

      const successCount = results.filter(
        (result) =>
          result.status === 'fulfilled' &&
          (result.value as UploadTenantFileResponse).status === 'success'
      ).length

      const failureCount = results.length - successCount

      if (failureCount === 0) {
        toast.success('All uploads successful!', {
          autoClose: 3000,
          hideProgressBar: false
        })
        // navigate('/hq/b2b')
      } else {
        toast.warning(
          `${successCount} uploads successful, ${failureCount} failed`,
          {
            autoClose: 5000,
            hideProgressBar: false
          }
        )
      }
    } catch (error) {
      console.error('Unexpected error during upload:', error)
      toast.error('Unexpected error during upload')
    }
  }

  const onSubmit = (data: FormValues) => {
    if (!tenantId) {
      toast.error(
        'Please complete the previous steps before uploading documents.',
        {
          autoClose: 5000,
          hideProgressBar: false
        }
      )
      return
    }

    handleFileUpload(data)
  }

  // Helper function to check if field has new file upload
  const hasNewFile = (fieldName: keyof FormValues) => {
    return watchedValues[fieldName] instanceof File
  }

  const hasExistingFile = (fieldName: keyof FormValues) => {
    return (
      typeof watchedValues[fieldName] === 'string' &&
      watchedValues[fieldName] !== ''
    )
  }

  const renderFileUpload = (
    name: keyof FormValues,
    label: string,
    gridSize: { md: number; xs: number } = { md: 6, xs: 12 }
  ) => {
    const status = uploadStatus[name]
    const existingFile = hasExistingFile(name)
    const newFile = hasNewFile(name)

    return (
      <Grid item md={gridSize.md} xs={gridSize.xs}>
        <Box>
          <Typography sx={{ mb: 1, fontWeight: 500 }}>{label}</Typography>

          {/* Show existing file info */}
          {existingFile && !newFile && (
            <Box
              sx={{
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 1,
                p: 2,
                mb: 2,
                backgroundColor: 'success.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant='body2' sx={{ color: 'success.main' }}>
                  Document already uploaded
                </Typography>
              </Box>
              <Button
                size='small'
                variant='outlined'
                onClick={() => {
                  window.open(watchedValues[name] as string, '_blank')
                }}
              >
                View
              </Button>
            </Box>
          )}

          {/* File upload area */}
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              position: 'relative',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'grey.50'
              },
              ...(status.uploaded && {
                borderColor: 'success.main',
                backgroundColor: 'success.50'
              }),
              ...(status.error && {
                borderColor: 'error.main',
                backgroundColor: 'error.50'
              }),
              ...(newFile && {
                borderColor: 'warning.main',
                backgroundColor: 'warning.50'
              })
            }}
          >
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <>
                  <input
                    type='file'
                    accept='image/png,.png'
                    disabled={status.loading}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        const mimeOk = file.type === 'image/png'
                        if (mimeOk) {
                          field.onChange(file)
                        } else {
                          toast.info('Only PNG is supported  ', {
                            autoClose: 2000,
                            hideProgressBar: false
                          })
                        }
                      }
                    }}
                    style={{ display: 'none' }}
                    id={`file-input-${name}`}
                  />
                  <label htmlFor={`file-input-${name}`}>
                    <Button
                      component='span'
                      variant='outlined'
                      startIcon={<UploadIcon />}
                      disabled={status.loading}
                      sx={{ mb: 1 }}
                    >
                      {existingFile && !newFile
                        ? 'Replace File'
                        : 'Choose File'}
                    </Button>
                  </label>

                  {newFile && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary' }}
                      >
                        Selected: {(watchedValues[name] as File).name}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() => {
                          field.onChange(
                            existingFile ? watchedValues[name] : null
                          )
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            />

            {status.uploaded && (
              <Typography
                variant='caption'
                sx={{ color: 'success.main', display: 'block', mt: 1 }}
              >
                ✓ Uploaded successfully
              </Typography>
            )}
          </Box>

          {status.error && (
            <Alert severity='error' sx={{ mt: 1 }}>
              {status.error}
            </Alert>
          )}
        </Box>
      </Grid>
    )
  }

  const hasNewUploads = Object.keys(watchedValues).some(
    (key) => watchedValues[key as keyof FormValues] instanceof File
  )

  const isUploading = Object.values(uploadStatus).some(
    (status) => status.loading
  )

  useEffect(() => {
    if (tenantData) {
      // Set existing file URLs from tenant data
      // Assuming tenantData has these fields with URLs
      setValue('companyLogo', tenantData.company_logo || null)
      setValue(
        'registrationCert',
        tenantData.business_registration_certificate || null
      )
      setValue('taxCert', tenantData.tax_identification_certificate || null)
      setValue('signatoryDoc', tenantData.authorized_signatory || null)

      // Update upload status to reflect existing files
      setUploadStatus((prev) => ({
        companyLogo: {
          ...prev.companyLogo,
          hasExisting: !!tenantData.company_logo
        },
        registrationCert: {
          ...prev.registrationCert,
          hasExisting: !!tenantData.business_registration_certificate
        },
        taxCert: {
          ...prev.taxCert,
          hasExisting: !!tenantData.tax_identification_certificate
        },
        signatoryDoc: {
          ...prev.signatoryDoc,
          hasExisting: !!tenantData.authorized_signatory
        }
      }))
    }
  }, [tenantData, setValue])

  return (
    <Box>
      {tenantLoading ? (
        <CircularProgress />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Badge>Only PNG files are allowed</Badge>
            <Grid container spacing={3}>
              {renderFileUpload('companyLogo', 'Company Logo')}
              {renderFileUpload(
                'registrationCert',
                'Business Registration Certificate',
                { md: 4, xs: 12 }
              )}
              {renderFileUpload(
                'taxCert',
                'Tax Identification Certificate (e.g. VAT / PAN / GST)',
                { md: 4, xs: 12 }
              )}
              {renderFileUpload(
                'signatoryDoc',
                'Authorized Signatory ID Proof (e.g., Passport, Driving License)',
                { md: 4, xs: 12 }
              )}
            </Grid>

            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                pt: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                marginTop: '20px'
              }}
            >
              <Button
                type='submit'
                variant='contained'
                disableElevation
                disabled={isUploading}
                size='large'
                sx={{
                  minWidth: 120,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {isUploading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Uploading...
                  </>
                ) : hasNewUploads ? (
                  'Upload New Files'
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Box>
  )
}
